import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import type {
  FormField, FormPage, FlowNodeData, FlowConnectionData, FlowValidationIssue, Condition, FieldRule, RuleCondition,
} from '../types/forms';
import { ruleOperatorToConditionOperator, conditionOperatorToRuleOperator, generateRuleId, generateConditionId } from '../lib/ruleUtils';
import { NON_INPUT_FIELD_TYPES } from '../types/forms';

// =====================================================
// CONSTANTS
// =====================================================

const NODE_WIDTH = 240;
const NODE_HEIGHT = 80;
const NODE_GAP_X = 120;
const BRANCH_GAP_Y = 60;
const TOP_MARGIN = 40;
const LEFT_MARGIN = 40;

// =====================================================
// TYPES
// =====================================================

export interface UseFlowEditorProps {
  fields: FormField[];
  onFieldsChange: (fields: FormField[]) => void;
  pages?: FormPage[];
}

export interface UseFlowEditorReturn {
  nodes: FlowNodeData[];
  connections: FlowConnectionData[];
  zoom: number;
  pan: { x: number; y: number };
  selectedNodeId: string | null;
  selectedConnectionId: string | null;
  validationIssues: FlowValidationIssue[];
  // Actions
  moveNode: (fieldId: string, position: { x: number; y: number }) => void;
  addConnection: (sourceFieldId: string, targetFieldId: string) => void;
  removeConnection: (connectionId: string) => void;
  updateConnectionCondition: (connectionId: string, condition: Condition | undefined) => void;
  selectNode: (fieldId: string | null) => void;
  selectConnection: (connectionId: string | null) => void;
  // Zoom/Pan
  zoomIn: () => void;
  zoomOut: () => void;
  fitToView: (containerWidth: number, containerHeight: number) => void;
  setZoom: (level: number) => void;
  setPan: (pan: { x: number; y: number }) => void;
  // Layout
  resetLayout: () => void;
}

// =====================================================
// AUTO-LAYOUT ALGORITHM
// =====================================================

function calculateAutoLayout(
  fields: FormField[],
  connections: FlowConnectionData[],
  existingNodes?: FlowNodeData[]
): FlowNodeData[] {
  if (!fields.length) return [];

  // Build adjacency for branching detection
  const outgoing = new Map<string, string[]>();
  connections.forEach(c => {
    if (!c.isDefault) {
      const list = outgoing.get(c.sourceFieldId) || [];
      list.push(c.targetFieldId);
      outgoing.set(c.sourceFieldId, list);
    }
  });

  // Preserve manual positions
  const manualPositions = new Map<string, { x: number; y: number }>();
  existingNodes?.forEach(n => {
    manualPositions.set(n.fieldId, n.position);
  });

  const nodes: FlowNodeData[] = [];
  const positioned = new Set<string>();

  // Horizontal left-to-right stacking with branch detection
  let x = LEFT_MARGIN;
  const centerY = TOP_MARGIN + NODE_HEIGHT; // Center row

  fields.forEach((field, index) => {
    if (positioned.has(field.id)) return;

    const manual = manualPositions.get(field.id);
    if (manual && existingNodes) {
      nodes.push({ fieldId: field.id, position: manual, width: NODE_WIDTH, height: NODE_HEIGHT });
    } else {
      // Check if this field has multiple branches
      const branches = outgoing.get(field.id) || [];

      nodes.push({
        fieldId: field.id,
        position: { x, y: centerY },
        width: NODE_WIDTH,
        height: NODE_HEIGHT,
      });

      // If branches exist, place them stacked vertically to the right
      if (branches.length > 1) {
        const branchX = x + NODE_WIDTH + NODE_GAP_X;
        const totalHeight = branches.length * (NODE_HEIGHT + BRANCH_GAP_Y) - BRANCH_GAP_Y;
        const startY = centerY - totalHeight / 2 + NODE_HEIGHT / 2;

        branches.forEach((targetId, branchIdx) => {
          if (!positioned.has(targetId)) {
            nodes.push({
              fieldId: targetId,
              position: { x: branchX, y: startY + branchIdx * (NODE_HEIGHT + BRANCH_GAP_Y) },
              width: NODE_WIDTH,
              height: NODE_HEIGHT,
            });
            positioned.add(targetId);
          }
        });
        x = branchX;
      }
    }

    positioned.add(field.id);
    x += NODE_WIDTH + NODE_GAP_X;
  });

  return nodes;
}

// =====================================================
// FLOW VALIDATION
// =====================================================

function validateFlow(
  fields: FormField[],
  connections: FlowConnectionData[]
): FlowValidationIssue[] {
  if (!fields.length) return [];

  const issues: FlowValidationIssue[] = [];
  const fieldIds = new Set(fields.map(f => f.id));

  // Build adjacency map (all connections including defaults)
  const adjacency = new Map<string, string[]>();
  connections.forEach(c => {
    const list = adjacency.get(c.sourceFieldId) || [];
    list.push(c.targetFieldId);
    adjacency.set(c.sourceFieldId, list);
  });

  // 1. Dead-ends: fields with no outgoing connection and not the last field
  const lastFieldId = fields[fields.length - 1]?.id;
  const thankYouIds = new Set(fields.filter(f => f.type === 'thank_you_screen').map(f => f.id));

  fields.forEach(field => {
    if (field.id === lastFieldId || thankYouIds.has(field.id)) return;
    if (NON_INPUT_FIELD_TYPES.includes(field.type)) return;

    const hasOutgoing = connections.some(c => c.sourceFieldId === field.id);
    if (!hasOutgoing) {
      // Check if next sequential field exists
      const idx = fields.findIndex(f => f.id === field.id);
      if (idx < fields.length - 1) return; // Has implicit sequential next

      issues.push({
        type: 'dead_end',
        fieldIds: [field.id],
        message: `Campo "${field.label}" não tem saída`,
      });
    }
  });

  // 2. Loop detection: DFS
  const visited = new Set<string>();
  const inStack = new Set<string>();

  function detectLoop(nodeId: string): boolean {
    if (inStack.has(nodeId)) return true;
    if (visited.has(nodeId)) return false;

    visited.add(nodeId);
    inStack.add(nodeId);

    for (const target of adjacency.get(nodeId) || []) {
      if (detectLoop(target)) {
        const sourceLabel = fields.find(f => f.id === nodeId)?.label || nodeId;
        const targetLabel = fields.find(f => f.id === target)?.label || target;
        issues.push({
          type: 'loop',
          fieldIds: [nodeId, target],
          message: `Loop detectado: "${sourceLabel}" → "${targetLabel}"`,
        });
        return true;
      }
    }

    inStack.delete(nodeId);
    return false;
  }

  fields.forEach(f => {
    if (!visited.has(f.id)) detectLoop(f.id);
  });

  // 3. Unreachable: BFS from first field
  const reachable = new Set<string>();
  const queue = [fields[0]?.id].filter(Boolean);

  while (queue.length) {
    const current = queue.shift()!;
    if (reachable.has(current)) continue;
    reachable.add(current);

    // Sequential next
    const idx = fields.findIndex(f => f.id === current);
    if (idx >= 0 && idx < fields.length - 1) {
      queue.push(fields[idx + 1].id);
    }

    // Conditional targets
    for (const target of adjacency.get(current) || []) {
      queue.push(target);
    }
  }

  fields.forEach(f => {
    if (!reachable.has(f.id) && f.id !== fields[0]?.id) {
      issues.push({
        type: 'unreachable',
        fieldIds: [f.id],
        message: `Campo "${f.label}" é inalcançável`,
      });
    }
  });

  return issues;
}

// =====================================================
// INITIALIZE FROM FIELDS (fields → flow)
// =====================================================

function initializeFromFields(
  fields: FormField[],
  existingNodes?: FlowNodeData[],
  pages?: FormPage[]
): { nodes: FlowNodeData[]; connections: FlowConnectionData[] } {
  const connections: FlowConnectionData[] = [];

  // Build pageId → first field lookup for resolving jump_to_page edges
  const pageFirstFieldId = new Map<string, string>();
  if (pages) {
    for (const page of pages) {
      const first = fields.find(f => f.pageId === page.id);
      if (first) pageFirstFieldId.set(page.id, first.id);
    }
  }

  fields.forEach((field, index) => {
    let hasJump = false;

    // NEW: Read from field.rules first (takes precedence)
    if (field.rules?.length) {
      for (const rule of field.rules) {
        for (const action of rule.actions) {
          // Build a Condition from the first rule condition (for display on the edge)
          const firstCond = rule.conditions[0];
          const condition: Condition | undefined = firstCond
            ? {
                fieldId: firstCond.fieldId,
                operator: ruleOperatorToConditionOperator(firstCond.operator),
                value: firstCond.value,
              }
            : undefined;

          if (action.type === 'jump_to') {
            connections.push({
              id: `conn_rule_${field.id}_${action.targetFieldId}_${rule.id}`,
              sourceFieldId: field.id,
              targetFieldId: action.targetFieldId,
              condition: rule.conditionType === 'if' ? condition : undefined,
              isDefault: rule.conditionType === 'always',
            });
            hasJump = true;
          } else if (action.type === 'jump_to_page') {
            // Resolve page → first field on that page for the visual edge
            const targetFieldId = pageFirstFieldId.get(action.targetPageId);
            if (targetFieldId) {
              connections.push({
                id: `conn_rule_${field.id}_page_${action.targetPageId}_${rule.id}`,
                sourceFieldId: field.id,
                targetFieldId,
                condition: rule.conditionType === 'if' ? condition : undefined,
                isDefault: rule.conditionType === 'always',
              });
              hasJump = true;
            }
          }
        }
      }
    }
    // EXISTING: Fall back to legacy conditionalLogic
    else {
      const logic = field.conditionalLogic;

      if (logic?.action === 'jump_to') {
        const conditionsWithJumps = (logic.conditions || []).filter(c => c.jumpTo);

        if (conditionsWithJumps.length > 0) {
          conditionsWithJumps.forEach(cond => {
            connections.push({
              id: `conn_${field.id}_${cond.jumpTo}_${cond.fieldId}`,
              sourceFieldId: field.id,
              targetFieldId: cond.jumpTo!,
              condition: { fieldId: cond.fieldId, operator: cond.operator, value: cond.value },
              isDefault: false,
            });
          });
          hasJump = true;
        } else if (logic.jumpTo) {
          connections.push({
            id: `conn_${field.id}_${logic.jumpTo}`,
            sourceFieldId: field.id,
            targetFieldId: logic.jumpTo,
            condition: logic.conditions?.[0],
            isDefault: false,
          });
          hasJump = true;
        }
      }
    }

    // Default sequential connection (alongside jump connections)
    if (index < fields.length - 1 && hasJump) {
      const nextFieldId = fields[index + 1].id;
      connections.push({
        id: `conn_default_${field.id}_${nextFieldId}`,
        sourceFieldId: field.id,
        targetFieldId: nextFieldId,
        isDefault: true,
      });
    }
  });

  const nodes = calculateAutoLayout(fields, connections, existingNodes);
  return { nodes, connections };
}

// =====================================================
// SYNC TO FIELDS (flow → fields)
// =====================================================

function syncToFields(
  fields: FormField[],
  connections: FlowConnectionData[]
): FormField[] {
  return fields.map(field => {
    // Find non-default connections from this field
    const conditionalConns = connections.filter(
      c => c.sourceFieldId === field.id && !c.isDefault && c.condition
    );

    // When field uses the new rules system, sync back to rules
    if (field.rules?.length) {
      return syncToFieldRules(field, conditionalConns, connections);
    }

    // Legacy: sync to conditionalLogic
    if (conditionalConns.length === 0) {
      if (field.conditionalLogic?.action === 'jump_to') {
        const { conditionalLogic, ...rest } = field;
        if (conditionalLogic?.dependsOn) {
          return { ...rest, conditionalLogic: { dependsOn: conditionalLogic.dependsOn, operator: conditionalLogic.operator, value: conditionalLogic.value } };
        }
        return rest as FormField;
      }
      return field;
    }

    const existingLogic = field.conditionalLogic || {};
    const conditions: Condition[] = conditionalConns.map(conn => ({
      ...conn.condition!,
      jumpTo: conn.targetFieldId,
    }));
    const primaryJumpTo = conditionalConns[0].targetFieldId;

    return {
      ...field,
      conditionalLogic: {
        ...existingLogic,
        action: 'jump_to' as const,
        jumpTo: primaryJumpTo,
        conditions,
        logicOperator: conditionalConns.length > 1 ? 'or' as const : (existingLogic.logicOperator || 'and'),
      },
    };
  });
}

/** Sync flow connections back into field.rules (preserving non-jump actions like calculations) */
function syncToFieldRules(
  field: FormField,
  conditionalConns: FlowConnectionData[],
  allConnections: FlowConnectionData[]
): FormField {
  const existingRules = field.rules || [];

  // Keep rules that have non-jump actions (calculations) — they are not managed by FlowEditor
  const nonJumpRules = existingRules.filter(rule =>
    rule.actions.every(a => a.type !== 'jump_to')
  );

  // Build "always" jump rules from default connections
  const alwaysConns = allConnections.filter(
    c => c.sourceFieldId === field.id && c.isDefault
  );
  const alwaysRules: FieldRule[] = alwaysConns.map(conn => ({
    id: generateRuleId(),
    conditionType: 'always' as const,
    conditions: [] as RuleCondition[],
    logicOperator: 'and' as const,
    actions: [{ type: 'jump_to' as const, targetFieldId: conn.targetFieldId }],
  }));

  // Build conditional jump rules from non-default connections
  const jumpRules: FieldRule[] = conditionalConns.map(conn => {
    const condition = conn.condition;
    const ruleConditions: RuleCondition[] = condition
      ? [{
          id: generateConditionId(),
          fieldId: condition.fieldId,
          operator: conditionOperatorToRuleOperator(condition.operator),
          value: condition.value ?? '',
        }]
      : [];

    return {
      id: generateRuleId(),
      conditionType: ruleConditions.length > 0 ? 'if' as const : 'always' as const,
      conditions: ruleConditions,
      logicOperator: 'and' as const,
      actions: [{ type: 'jump_to' as const, targetFieldId: conn.targetFieldId }],
    };
  });

  // Condicionais antes da 'always': a conexão default é o fallback do nó. O motor
  // (resolveFieldRules) já garante essa precedência, mas gravar nessa ordem mantém
  // o Editor de Regras mostrando a rota principal no topo.
  return {
    ...field,
    rules: [...nonJumpRules, ...jumpRules, ...alwaysRules],
  };
}

// =====================================================
// HOOK
// =====================================================

export function useFlowEditor({
  fields,
  onFieldsChange,
  pages,
}: UseFlowEditorProps): UseFlowEditorReturn {
  const [nodes, setNodes] = useState<FlowNodeData[]>([]);
  const [connections, setConnections] = useState<FlowConnectionData[]>([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const initialized = useRef(false);

  // Refs for stable access inside state updaters (avoids stale closures)
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const onFieldsChangeRef = useRef(onFieldsChange);
  onFieldsChangeRef.current = onFieldsChange;

  // Initialize from fields on mount or when fields change structurally
  useEffect(() => {
    const fieldIds = fields.map(f => f.id).join(',');
    const nodeIds = nodes.map(n => n.fieldId).join(',');

    if (!initialized.current || fieldIds !== nodeIds) {
      const result = initializeFromFields(fields, initialized.current ? nodes : undefined, pages);
      setNodes(result.nodes);
      setConnections(result.connections);
      initialized.current = true;
    }
  }, [fields]);

  // Validation (memoized)
  const validationIssues = useMemo(
    () => validateFlow(fields, connections),
    [fields, connections]
  );

  // Move node
  const moveNode = useCallback((fieldId: string, position: { x: number; y: number }) => {
    setNodes(prev => prev.map(n =>
      n.fieldId === fieldId ? { ...n, position } : n
    ));
  }, []);

  // Add connection
  const addConnection = useCallback((sourceFieldId: string, targetFieldId: string) => {
    const id = `conn_${sourceFieldId}_${targetFieldId}_${Date.now()}`;
    const newConn: FlowConnectionData = {
      id,
      sourceFieldId,
      targetFieldId,
      isDefault: false,
    };

    setConnections(prev => {
      const updated = [...prev, newConn];
      const updatedFields = syncToFields(fieldsRef.current, updated);
      onFieldsChangeRef.current(updatedFields);
      return updated;
    });
    setSelectedConnectionId(id);
  }, []);

  // Remove connection
  const removeConnection = useCallback((connectionId: string) => {
    setConnections(prev => {
      const updated = prev.filter(c => c.id !== connectionId);
      const updatedFields = syncToFields(fieldsRef.current, updated);
      onFieldsChangeRef.current(updatedFields);
      return updated;
    });
    setSelectedConnectionId(prev => prev === connectionId ? null : prev);
  }, []);

  // Update connection condition
  //
  // `isDefault` acompanha a condição: pôr condição numa ligação transforma-a em
  // rota condicional; tirar a condição devolve-a a rota default (é o que faz o
  // botão "Tornar default"). Sem isso, configurar uma condição numa ligação
  // marcada Default não tinha efeito nenhum — a sincronização ignora ligações
  // default e a condição era descartada em silêncio.
  const updateConnectionCondition = useCallback((
    connectionId: string,
    condition: Condition | undefined
  ) => {
    setConnections(prev => {
      const updated = prev.map(c =>
        c.id === connectionId ? { ...c, condition, isDefault: !condition } : c
      );
      const updatedFields = syncToFields(fieldsRef.current, updated);
      onFieldsChangeRef.current(updatedFields);
      return updated;
    });
  }, []);

  // Zoom
  const zoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.1, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.1, 0.3));
  }, []);

  const fitToView = useCallback((containerWidth: number, containerHeight: number) => {
    if (!nodes.length) return;

    const minX = Math.min(...nodes.map(n => n.position.x));
    const maxX = Math.max(...nodes.map(n => n.position.x + n.width));
    const minY = Math.min(...nodes.map(n => n.position.y));
    const maxY = Math.max(...nodes.map(n => n.position.y + n.height));

    const contentWidth = maxX - minX + 80;
    const contentHeight = maxY - minY + 80;

    const scaleX = containerWidth / contentWidth;
    const scaleY = containerHeight / contentHeight;
    const idealZoom = Math.min(scaleX, scaleY);

    // For large flows (>10 nodes), ensure readable zoom — show beginning of flow
    const MIN_READABLE_ZOOM = 0.55;
    if (idealZoom < MIN_READABLE_ZOOM) {
      setZoom(MIN_READABLE_ZOOM);
      setPan({
        x: -(minX - 40) * MIN_READABLE_ZOOM + 40,
        y: -(minY - 40) * MIN_READABLE_ZOOM + (containerHeight - contentHeight * MIN_READABLE_ZOOM) / 2,
      });
    } else {
      const newZoom = Math.min(idealZoom, 1.5);
      setZoom(newZoom);
      setPan({
        x: -(minX - 40) * newZoom + (containerWidth - contentWidth * newZoom) / 2,
        y: -(minY - 40) * newZoom + (containerHeight - contentHeight * newZoom) / 2,
      });
    }
  }, [nodes]);

  // Reset layout
  const resetLayout = useCallback(() => {
    const result = calculateAutoLayout(fields, connections);
    setNodes(result);
  }, [fields, connections]);

  // Select
  const selectNode = useCallback((fieldId: string | null) => {
    setSelectedNodeId(fieldId);
    if (fieldId) setSelectedConnectionId(null);
  }, []);

  const selectConnection = useCallback((connectionId: string | null) => {
    setSelectedConnectionId(connectionId);
    if (connectionId) setSelectedNodeId(null);
  }, []);

  return {
    nodes,
    connections,
    zoom,
    pan,
    selectedNodeId,
    selectedConnectionId,
    validationIssues,
    moveNode,
    addConnection,
    removeConnection,
    updateConnectionCondition,
    selectNode,
    selectConnection,
    zoomIn,
    zoomOut,
    fitToView,
    setZoom,
    setPan,
    resetLayout,
  };
}
